import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Detail } from 'src/app/common-components/detail/detail';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { getPromptPrivileges } from 'src/app/privileges/privileges.selector';
import { PromptDetailSaveEvent } from '../models/prompt-detail-form-submit-event';
import { PromptDetailSaveModel } from '../models/prompt-detail-save.model';
import { PromptDetailView } from '../models/prompt-detail-view.model';
import { PromptPrivileges } from '../models/prompt-privileges';
import { PromptDetailFormService } from '../services/prompt-detail-form.service';
import * as PromptDetailActions from '../state/prompt-detail.actions';
import { getPrompt, getPromptCallState } from '../state/prompt-detail.selectors';

@Component({
   encapsulation: ViewEncapsulation.None,
   host: { class: 'entity-detail' },
   providers: [PromptDetailFormService, { provide: FormsBase, useExisting: PromptDetailFormService }],
   selector: 'ndt-prompt-detail',
   templateUrl: './prompt-detail.component.html',
   standalone: false,
})
export class PromptDetailComponent extends Detail<PromptDetailFormService> implements OnDestroy, OnInit {
   prompt$: Observable<PromptDetailView | null>;

   callState$: Observable<CallState>;

   promptPrivileges$: Observable<PromptPrivileges>;

   originalFileId: number | null;

   originalPrompt: PromptDetailView | null;

   promptUpdatedViaCallToRecord: number | null = null;

   newFileId: number | null = null;

   constructor(public store: Store, protected formService: PromptDetailFormService) {
      super(store, formService);
      this.prompt$ = this.store.pipe(select(getPrompt));
      this.callState$ = this.store.pipe(select(getPromptCallState));
      this.promptPrivileges$ = this.store.pipe(select(getPromptPrivileges));
      this.store.dispatch(PromptDetailActions.promptDetailLoad());
   }

   ngOnDestroy(): void {
      //Only revert changes on destroy, Navigation events fire before our route guards
      this.revertUnsavedPromptFileChange();
      this.destroyed$.next();
   }

   ngOnInit(): void {
      this.prompt$.pipe(takeUntil(this.destroyed$)).subscribe((prompt) => {
         this.originalPrompt = prompt;
         this.originalFileId = prompt?.file?.fileId ?? null;
      });
   }

   // Updating a prompt file via calling an extension or external number saves the new fileId to the prompt, but we want the user to need to save the prompt to confirm the changes. If the user navigates away from the prompt detail page without saving the prompt, the prompt's fileId should be reverted back to the original fileId and the new file should be deleted.
   private revertUnsavedPromptFileChange(): void {
      if (this.newFileId === null || this.originalFileId === null) {
         return;
      }

      this.store.dispatch(
         PromptDetailActions.revertPromptFileUpdateOnNavigate({
            prompt: this.initializePromptForFileRevert(),
            fileId: this.newFileId,
         })
      );

      this.newFileId = null;
   }

   private initializePromptForFileRevert(): PromptDetailSaveEvent {
      let promptData: PromptDetailSaveModel = {
         promptId: this.originalPrompt?.promptId ?? null,
         fileId: this.originalFileId,
         dontDeleteFileOnUpdate: true,
         copyMohFile: false,
         accountId: this.originalPrompt?.account?.accountId ?? null,
         file: null,
         isActive: this.originalPrompt?.isActive ?? false,
         isReusable: this.originalPrompt?.isReusable ?? false,
         name: this.originalPrompt?.name ?? '',
         typeId: this.originalPrompt?.typeId ?? null,
      };

      let prompt: PromptDetailSaveEvent = {
         prompt: promptData,
         promptFields: ['fileId'],
         file: null,
         routeToNewPromptAfterSave: null,
         voiceMailGreetingPrompt: null,
      };

      return prompt;
   }

   updateNewFileId(fileId: number): void {
      this.newFileId = fileId;
   }

   onDeletePrompt(event: { promptId: number; name: string }) {
      this.store.dispatch(PromptDetailActions.deletePrompt(event));
   }

   onPromptUpdatedViaCallToRecord(event: number | null) {
      //promptUpdatedViaCallToRecord stores prompt's pre-update fileId to delete file after prompt is saved to confirm changes (call to record updates prompt file without saving prompt detail form)
      this.promptUpdatedViaCallToRecord = event;
   }

   onFormSaveCanceled(): void {
      this.store.dispatch(PromptDetailActions.promptDetailCancel());
      this.newFileId = null;
      this.promptUpdatedViaCallToRecord = null;
   }

   onCancelFormSaveIfNewFile(): void {
      let prompt = this.initializePromptForFileRevert();

      //save prompt back to original fileId and then delete new file
      this.store.dispatch(
         PromptDetailActions.revertPromptFileUpdateOnNavigate({
            prompt,
            fileId: this.newFileId ?? 0,
         })
      );

      this.newFileId = null;
      this.promptUpdatedViaCallToRecord = null;
   }

   onFormSubmitted(event: PromptDetailSaveEvent): void {
      if (this.promptUpdatedViaCallToRecord != null) {
         this.store.dispatch(
            PromptDetailActions.deleteNewFileIfPromptSaveCanceled({ fileId: this.promptUpdatedViaCallToRecord })
         );
      }
      this.store.dispatch(PromptDetailActions.savePromptDetail(event));
      this.newFileId = null;
      this.promptUpdatedViaCallToRecord = null;
   }
}
